/**
 * Search behind the smart choice fields (`SmartSelect`, `SmartMultiSelect`, the grid's lookup editor; UX12): what the
 * user types is compared with an option's code, name and keywords after both sides are normalised, so that
 *
 * - Arabic spelling variants match: أ إ آ ٱ → ا, ة → ه, ى → ي, without tashkeel (harakat, shadda, sukun) or tatweel;
 * - Arabic-Indic (٠١٢…) and Persian (۰۱۲…) digits match Latin ones;
 * - case and Latin accents do not matter;
 * - several words must all appear, in any order ("نقد صندوق" finds "صندوق النقد الرئيسي").
 *
 * Matches are ranked: an exact code first, then codes and words that start with the typed text, then anything that
 * merely contains it; equal matches keep the list's own order.
 */

/** One choice of a smart field. */
export interface SmartOption {
  /** Stored value (an id or a code). */
  value: string;
  /** Text shown in the list and in the field once chosen, e.g. "1101 — الصندوق". */
  label: string;
  /** Code searched and shown before the label when the label does not contain it already. */
  code?: string;
  /** Secondary text shown at the end of the row (unit, balance, branch); it is searched too. */
  detail?: string;
  /** Further words that find the option without being shown (an English name, a barcode, an old code). */
  keywords?: string;
  /** Shown but cannot be chosen. */
  disabled?: boolean;
}

const tashkeel = /[ؐ-ًؚ-ٰٟۖ-ۜ۟-۪ۨ-ۭـ]/u;
const latinMarks = /[̀-ͯ]/u;

/** The normalised form of one character: "" when it is dropped (tashkeel, tatweel, a Latin accent). */
function normaliseChar(char: string): string {
  if (tashkeel.test(char)) return "";
  const code = char.codePointAt(0) ?? 0;
  // Arabic-Indic ٠-٩ and extended (Persian / Urdu) ۰-۹ digits.
  if (code >= 0x0660 && code <= 0x0669) return String(code - 0x0660);
  if (code >= 0x06f0 && code <= 0x06f9) return String(code - 0x06f0);
  switch (char) {
    case "أ":
    case "إ":
    case "آ":
    case "ٱ":
      return "ا";
    case "ة":
      return "ه";
    case "ى":
      return "ي";
    case " ":
      return " ";
    default:
      break;
  }
  if (code < 0x80) return char.toLowerCase();
  return char.normalize("NFD").replace(latinMarks, "").toLowerCase();
}

/** Normalised text plus, for each of its characters, the index of the original character it came from. */
export interface PreparedText {
  text: string;
  sources: readonly number[];
}

/** Normalises `text` keeping where every character came from (to highlight the original text). */
export function prepareText(text: string): PreparedText {
  let normalised = "";
  const sources: number[] = [];
  let index = 0;
  for (const char of text) {
    const mapped = normaliseChar(char);
    if (mapped !== "") sources.push(...Array.from(mapped, () => index));
    normalised += mapped;
    index += char.length;
  }
  return { text: normalised, sources };
}

/** The normalised form of `text` used for comparing. */
export function normaliseSearchText(text: string): string {
  let normalised = "";
  for (const char of text) normalised += normaliseChar(char);
  return normalised;
}

/** The normalised words of a query; empty when the query is blank. */
export function searchTokens(query: string): string[] {
  return normaliseSearchText(query)
    .split(/\s+/u)
    .filter((token) => token !== "");
}

interface Indexed {
  code: string;
  label: string;
  haystack: string;
}

const indexCache = new WeakMap<SmartOption, Indexed>();

function indexed(option: SmartOption): Indexed {
  let entry = indexCache.get(option);
  if (!entry) {
    const code = normaliseSearchText(option.code ?? "");
    const label = normaliseSearchText(option.label);
    const haystack = [code, label, normaliseSearchText(option.detail ?? ""), normaliseSearchText(option.keywords ?? "")]
      .filter((part) => part !== "")
      .join("\n");
    entry = { code, label, haystack };
    indexCache.set(option, entry);
  }
  return entry;
}

/** A word of `text` starts at `position`: start of text, after a space / punctuation, or after the article "ال". */
function atWordStart(text: string, position: number, article = true): boolean {
  if (position === 0) return true;
  if (/[\s\-–—_/.,:;()[\]|#]/u.test(text.charAt(position - 1))) return true;
  return (
    article && position >= 2 && text.slice(position - 2, position) === "ال" && atWordStart(text, position - 2, false)
  );
}

/**
 * How well `option` matches the query words: `null` when a word is missing, otherwise a rank (lower is better).
 */
export function matchRank(option: SmartOption, tokens: readonly string[]): number | null {
  if (tokens.length === 0) return 0;
  const { code, label, haystack } = indexed(option);
  const whole = tokens.join(" ");
  let rank = 0;
  if (code !== "" && code === whole) rank -= 100;
  else if (code !== "" && code.startsWith(whole)) rank -= 60;
  else if (label === whole) rank -= 50;
  else if (label.startsWith(whole)) rank -= 40;
  for (const token of tokens) {
    let position = haystack.indexOf(token);
    if (position < 0) return null;
    let wordStart = false;
    while (position >= 0) {
      if (atWordStart(haystack, position)) {
        wordStart = true;
        break;
      }
      position = haystack.indexOf(token, position + 1);
    }
    rank += wordStart ? 0 : 10;
  }
  return rank;
}

/** True when the option matches every word of `query`. */
export function matchesQuery(option: SmartOption, query: string): boolean {
  return matchRank(option, searchTokens(query)) !== null;
}

export interface FilterResult<T extends SmartOption> {
  /** The best matches, at most `limit` of them. */
  matches: T[];
  /** How many options match in all. */
  total: number;
}

/** The options matching `query`, best first, at most `limit` (all options when the query is blank). */
export function filterOptions<T extends SmartOption>(
  options: readonly T[],
  query: string,
  limit = Number.POSITIVE_INFINITY,
): FilterResult<T> {
  const tokens = searchTokens(query);
  if (tokens.length === 0) return { matches: options.slice(0, limit), total: options.length };
  const ranked: { option: T; rank: number; order: number }[] = [];
  options.forEach((option, order) => {
    const rank = matchRank(option, tokens);
    if (rank !== null) ranked.push({ option, rank, order });
  });
  ranked.sort((a, b) => a.rank - b.rank || a.order - b.order);
  return { matches: ranked.slice(0, limit).map((entry) => entry.option), total: ranked.length };
}

/** The option whose code or label is exactly `text` (normalised), if any: a typed code picks its record. */
export function exactOption<T extends SmartOption>(options: readonly T[], text: string): T | undefined {
  const wanted = normaliseSearchText(text.trim());
  if (wanted === "") return undefined;
  return (
    options.find((option) => indexed(option).code === wanted) ??
    options.find((option) => normaliseSearchText(option.value) === wanted) ??
    options.find((option) => indexed(option).label === wanted)
  );
}

/** Ranges `[start, end)` of `text` that match a word of `query`, sorted and merged (for highlighting). */
export function highlightRanges(text: string, query: string): [number, number][] {
  const tokens = searchTokens(query);
  if (tokens.length === 0 || text === "") return [];
  const prepared = prepareText(text);
  const ranges: [number, number][] = [];
  for (const token of tokens) {
    let position = prepared.text.indexOf(token);
    while (position >= 0) {
      const start = prepared.sources[position] ?? 0;
      const lastSource = prepared.sources[position + token.length - 1] ?? start;
      // The end takes the whole last character and any dropped marks (tashkeel) right after it.
      let end = lastSource + 1;
      const next = prepared.sources[position + token.length];
      if (next === undefined) end = text.length;
      else end = Math.max(end, next);
      ranges.push([start, end]);
      position = prepared.text.indexOf(token, position + token.length);
    }
  }
  ranges.sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  for (const range of ranges) {
    const last = merged.at(-1);
    if (last && range[0] <= last[1]) last[1] = Math.max(last[1], range[1]);
    else merged.push([range[0], range[1]]);
  }
  return merged;
}

/**
 * Both names of a record as search keywords, so that an Arabic screen finds a record by its English name and the other
 * way round (the label shows only the name of the screen's language).
 */
export function nameKeywords(record: { nameAr?: string | null; nameEn?: string | null }): string {
  return [record.nameAr ?? "", record.nameEn ?? ""].filter((name) => name !== "").join(" ");
}

/** The code is shown before the label only when the label does not already contain it. */
export function shownCode(option: SmartOption): string | null {
  const code = option.code?.trim() ?? "";
  if (code === "" || option.label.includes(code)) return null;
  return code;
}

/** `text` split into plain and matching parts for rendering. */
export function highlightParts(text: string, query: string): { text: string; match: boolean }[] {
  const ranges = highlightRanges(text, query);
  if (ranges.length === 0) return [{ text, match: false }];
  const parts: { text: string; match: boolean }[] = [];
  let cursor = 0;
  for (const [start, end] of ranges) {
    if (start > cursor) parts.push({ text: text.slice(cursor, start), match: false });
    parts.push({ text: text.slice(start, end), match: true });
    cursor = end;
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor), match: false });
  return parts;
}
